import { Lms, PlatformInfo } from "@verity/api-client/types";
import { VerityInstituteSettings } from "@verity/components";
import React, { ReactNode } from "react";
import { Provider } from "react-redux";
import { BrowserType, isPlatformCompatible } from "../helpers/device";
import { canAccessResource, UserPrivileges } from "../helpers/privilege";
import i18n from "../i18n";
import { isInstructor, useIsAdmin } from "./shared";

import { AssessmentReport } from "../reports/assessment-report";
import { CourseReport } from "../reports/course-report";
import { InstitutionReport } from "../reports/institution-report";
import { UsageReport } from "../reports/usage-report";
import AssessmentsPage from "./components/assessments-page";
import createStudentAssessmentsPageStore from "./components/assessments-page/student-page/store";
import ExtensionSetupPage from "./components/extension-setup-page";
import { InstitutionInformation } from "./components/institution-information/InstitutionInformation";
import { IntegrationSettings } from "./components/integration-settings/IntegrationSettings";
import LatestUpdates from "./components/latest-updates";
import LivePopInPage from "./components/live-pop-in/LivePopInPage";
import LiveProctoringPage from "./components/live-proctoring";
import LockdownSetupPage from "./components/lockdown-setup-page";
import Marketplace from "./components/marketplace";
import MobileSetupPage from "./components/mobile-setup-page";
import MobileWorkflow from "./components/mobile-workflow";
import RosterPage from "./components/roster-page";
import {
	InstituteScopeSettingsPortal,
	UserSettingsPortal,
	VerityCourseSettingsPortal,
	VerityInstituteSettingsPortal,
	VerityThirdPartyIntegrationPortal,
} from "./tab-settings";

import { getProduct } from "../helpers/url";
import CourseSvg from "../images/portal-navigation/assessmentReport.svg?react";
import AssessmentsSvg from "../images/portal-navigation/assessments.svg?react";
import BookIcon from "../images/portal-navigation/bookIcon.svg?react";
import ChecklistSvg from "../images/portal-navigation/checklist.svg?react";
import ConfigurationSvg from "../images/portal-navigation/configuration.svg?react";
import CourseSettingsSvg from "../images/portal-navigation/courseSettings.svg?react";
import ExtensionSvg from "../images/portal-navigation/extension.svg?react";
import HubSvg from "../images/portal-navigation/hub.svg?react";
import InstitutionSvg from "../images/portal-navigation/institution.svg?react";
import InstitutionInformationSvg from "../images/portal-navigation/institutionInformation.svg?react";
import InstitutionSettingsSvg from "../images/portal-navigation/institutionSettings.svg?react";
import IntegrationSettingsSvg from "../images/portal-navigation/integrations.svg?react";
import LatestUpdatesSvg from "../images/portal-navigation/latestUpdates.svg?react";
import LiveSvg from "../images/portal-navigation/liveGroup.svg?react";
import LiveScheduleSvg from "../images/portal-navigation/liveSchedule.svg?react";
import LockdownSvg from "../images/portal-navigation/lockdown.svg?react";
import MarketplaceSvg from "../images/portal-navigation/marketplace.svg?react";
import MobileSvg from "../images/portal-navigation/mobileIcon.svg?react";
import PopInMonitoringSvg from "../images/portal-navigation/popInMonitoring.svg?react";
import ReportsSvg from "../images/portal-navigation/reports.svg?react";
import ScopeSettingSvg from "../images/portal-navigation/scopeSettingSidebarIcon.svg?react";
import SettingsSvg from "../images/portal-navigation/settings.svg?react";
import SettingsRosterIcon from "../images/portal-navigation/settingsRosterIcon.svg?react";
import ThirdPartyIntegrationSvg from "../images/portal-navigation/thirdPartyIntegration.svg?react";
import UsageSvg from "../images/portal-navigation/usage.svg?react";
import UserSettingsSvg from "../images/portal-navigation/user-gear.svg?react";
import SetupSvg from "../images/portal-navigation/userSetup.svg?react";
import InterviewsHomePage from "./components/interview-hub";
import InterviewPage from "./components/interview-hub/InterviewPage/InterviewPage";
import CompletePage from "./components/interview-hub/InterviewPage/pages/CompletePage/CompletePage";
import InterviewResultsTable from "./components/interview-hub/InterviewResults/InterviewResultsTable";
import InterviewResultsPage from "./components/interview-hub/InterviewResults/pages/InterviewResultsPage";
import SuccessPage from "./components/interview-hub/ScheduleInterview/pages/SuccessPage/SuccessPage";
import ScheduleInterview from "./components/interview-hub/ScheduleInterview/ScheduleInterview";
import TakeAssessmentPage from "./components/take-assessment";

export type PortalContext = {
	instituteId?: number;
	instituteUuid?: string;
	instituteIntegrationOrigin?: string;
	instituteIntegrationHostname?: string;
	portalOrigin?: string;
	portalHostname?: string;
	homeOrigin: string;
	lmsProvidedCourseId?: string;
	courseId?: number;
	courseUuid?: string;
} & (
	| { lms?: Lms; lmsDetails?: Record<string, unknown> }
	| {
			lms: "BlackboardLearn";
			lmsDetails?: { type: "original" | "ultra" };
	  }
);

export type UserPortalContext = PortalContext & {
	assessmentId?: number;
};

export type PortalPageKey =
	| "assessments"
	| "liveSessions"
	| "livePopInMonitoring"
	| "extensionSetup"
	| "lockdownSetup"
	| "mobileSetup"
	| "institutionReport"
	| "courseReport"
	| "assessmentReport"
	| "usageReport"
	| "thirdPartyIntegration"
	| "instituteSettings"
	| "courseSettings"
	| "integrationSettings"
	| "scopeSettings"
	| "userSettings"
	| "mobileWorkflow"
	| "rosterManagement"
	| "institutionInformation"
	| "marketplace"
	| "latestUpdates"
	| "takeAssessment";

type PortalPageRoutes = PortalPageKey | "takeAssessment/:quizId";

export type CandidatePageKey =
	| "interviews"
	| "scheduleInterview"
	| "interviews/schedule"
	| "scheduleInterviewSubmitted"
	| "interviews/schedule/submitted"
	| "attendInterview"
	| "interviews/:id"
	| "completedInterview"
	| "interviews/:sessionId/completed"
	| "resultsTable"
	| "results"
	| "results/:id"
	| "reports"
	| "configuration"
	| "integrationSettings"
	| "courseSettings"
	| "rosterManagement";

/**
 * `tabLink` is legacy support for any tabs that are link-based and need to be displayed in an iframe.
 */
export type PortalPageInfo = {
	key: PortalPageRoutes | CandidatePageKey;
	isAllowed?: (
		params: PortalContext &
			UserPortalContext & {
				verityInstituteSettings: VerityInstituteSettings;
				privileges: UserPrivileges;
				platformInfo: PlatformInfo;
				browserType: BrowserType;
			},
	) => boolean;
	isDefault?: (
		params: PortalContext & {
			platformInfo: PlatformInfo;
			browserType: BrowserType;
			privileges: UserPrivileges;
			allowedPages: Set<PortalPageKey>;
		},
	) => boolean;
} & (
	| { component: (context: PortalContext) => React.JSX.Element | undefined; link?: undefined }
	| {
			link: (context: PortalContext) => string;
			component?: undefined;
	  }
);

const studentAssessmentsPageStore = createStudentAssessmentsPageStore();

export const candidatePages: Partial<Record<CandidatePageKey, PortalPageInfo>> = {
	interviews: {
		key: "interviews",
		component: () => <InterviewsHomePage />,
		isAllowed: () => true,
	},
	"interviews/schedule": {
		key: "interviews/schedule",
		component: () => <ScheduleInterview />,
		isAllowed: () => true,
	},
	scheduleInterviewSubmitted: {
		key: "interviews/schedule/submitted",
		component: () => <SuccessPage />,
		isAllowed: () => true,
	},
	attendInterview: {
		key: "interviews/:id",
		component: () => <InterviewPage />,
		isAllowed: () => true,
	},
	completedInterview: {
		key: "interviews/:sessionId/completed",
		component: () => <CompletePage />,
		isAllowed: () => true,
	},
	resultsTable: {
		key: "results",
		component: () => <InterviewResultsTable />,
		isAllowed: () => true,
	},
	results: {
		key: "results/:id",
		component: () => <InterviewResultsPage />,
		isAllowed: () => true,
	},
	reports: {
		key: "reports",
		component: () => <></>,
		isAllowed: () => true,
	},
	integrationSettings: {
		key: "integrationSettings",
		component: ({ instituteId }) => (instituteId ? <IntegrationSettings instituteId={instituteId} /> : undefined),
		isAllowed: () => true,
	},
	rosterManagement: {
		key: "rosterManagement",
		component: () => <RosterPage />,
		isAllowed: ({ instituteUuid, privileges }) =>
			!!instituteUuid &&
			(canAccessResource(
				{ privileges },
				{
					privilege: "USER",
					action: "CREATE",
					resourceScope: { instituteId: instituteUuid },
				},
			) ||
				canAccessResource(
					{ privileges },
					{
						privilege: "USER",
						action: "UPDATE",
						resourceScope: { instituteId: instituteUuid },
					},
				)),
	},
	courseSettings: {
		key: "courseSettings",
		component: VerityCourseSettingsPortal,
		isAllowed: ({ instituteUuid, verityInstituteSettings, courseUuid, lmsProvidedCourseId, privileges }) =>
			!!instituteUuid &&
			!!lmsProvidedCourseId &&
			!verityInstituteSettings.liteMode &&
			canAccessResource(
				{ privileges },
				{
					privilege: "COURSE",
					action: "UPDATE",
					resourceScope: { instituteId: instituteUuid, courseId: courseUuid, resourceId: courseUuid },
				},
			),
	},
};

export const portalPages: Record<PortalPageKey, PortalPageInfo> = {
	assessments: {
		key: "assessments",
		component: () => (
			<Provider store={studentAssessmentsPageStore}>
				<AssessmentsPage />
			</Provider>
		),
		isAllowed: ({ instituteUuid, courseUuid, privileges }) =>
			!!instituteUuid &&
			!!courseUuid &&
			canAccessResource(
				{ privileges },
				{
					privilege: "ASSESSMENTS",
					action: "READ",
					resourceScope: { instituteId: instituteUuid, courseId: courseUuid },
				},
			),
		isDefault: ({ lms, platformInfo, privileges, ...context }) =>
			!lms ||
			(platformInfo.platform !== "none" && (platformInfo.platform !== "extension" || platformInfo.versionSupported)) ||
			!isInstructor(privileges, context),
	},
	liveSessions: {
		key: "liveSessions",
		component: () => <LiveProctoringPage />,
		isAllowed: ({ lms, instituteUuid, courseUuid, lmsProvidedCourseId, verityInstituteSettings, privileges }) =>
			!!lms &&
			!!instituteUuid &&
			!!lmsProvidedCourseId &&
			!verityInstituteSettings.liteMode &&
			verityInstituteSettings.allowLiveProctoring &&
			canAccessResource(
				{ privileges },
				{
					privilege: "LIVE_SESSION",
					action: "UPDATE",
					resourceScope: { instituteId: instituteUuid, courseId: courseUuid },
				},
			),
	},
	livePopInMonitoring: {
		key: "livePopInMonitoring",
		component: ({ instituteId }) => (instituteId ? <LivePopInPage instituteId={instituteId} /> : undefined),
		isAllowed: ({ lms, instituteId, instituteUuid, verityInstituteSettings, privileges }) =>
			!!lms &&
			instituteId != null &&
			!!instituteUuid &&
			!verityInstituteSettings.liteMode &&
			verityInstituteSettings.allowLivePopIn &&
			canAccessResource(
				{ privileges },
				{
					privilege: "LIVE_SESSION",
					action: "UPDATE",
					resourceScope: { instituteId: instituteUuid },
				},
			),
	},
	extensionSetup: {
		key: "extensionSetup",
		component: () => <ExtensionSetupPage />,
		isAllowed: ({ verityInstituteSettings, browserType }) =>
			verityInstituteSettings.allowExtensionPlatform && isPlatformCompatible("extension", browserType),
		isDefault: ({ lms, platformInfo, browserType, privileges, allowedPages, ...context }) =>
			isInstructor(privileges, context) &&
			((lms && platformInfo.platform === "none" && isPlatformCompatible("extension", browserType)) ||
				(platformInfo.platform === "extension" && !platformInfo.versionSupported) ||
				!allowedPages.has("assessments")),
	},
	lockdownSetup: {
		key: "lockdownSetup",
		component: () => <LockdownSetupPage />,
		isAllowed: ({ verityInstituteSettings, browserType }) =>
			verityInstituteSettings.allowLockdownPlatform && isPlatformCompatible("lockdown", browserType),
		isDefault: ({ privileges, allowedPages, ...context }) =>
			isInstructor(privileges, context) && !allowedPages.has("assessments") && !allowedPages.has("extensionSetup"),
	},
	mobileSetup: {
		key: "mobileSetup",
		component: () => <MobileSetupPage />,
		isAllowed: ({ verityInstituteSettings, browserType }) =>
			verityInstituteSettings.allowMobilePlatform &&
			(isPlatformCompatible("mobile", browserType) || browserType === "desktop-browser"),
		isDefault: ({ privileges, allowedPages, ...context }) =>
			isInstructor(privileges, context) &&
			!allowedPages.has("assessments") &&
			!allowedPages.has("extensionSetup") &&
			!allowedPages.has("lockdownSetup"),
	},
	institutionReport: {
		key: "institutionReport",
		component: ({ instituteId }) => (instituteId ? <InstitutionReport instituteId={instituteId} /> : undefined),
		isAllowed: ({ instituteId, instituteUuid, verityInstituteSettings, privileges }) =>
			instituteId != null &&
			!(verityInstituteSettings.liteMode && !verityInstituteSettings.liteModeViewReports) &&
			canAccessResource(
				{ privileges },
				{
					privilege: "INSTITUTE_REPORT",
					action: "READ",
					resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
				},
			),
	},
	courseReport: {
		key: "courseReport",
		component: ({ instituteId, lmsProvidedCourseId }) => (
			<CourseReport instituteId={instituteId} lmsCourseId={lmsProvidedCourseId} />
		),
		isAllowed: ({ instituteId, instituteUuid, courseUuid, lmsProvidedCourseId, verityInstituteSettings, privileges }) =>
			instituteId != null &&
			!!lmsProvidedCourseId &&
			!(verityInstituteSettings.liteMode && !verityInstituteSettings.liteModeViewReports) &&
			canAccessResource(
				{ privileges },
				{
					privilege: "COURSE_REPORT",
					action: "READ",
					resourceScope: { instituteId: instituteUuid, courseId: courseUuid, resourceId: courseUuid },
				},
			),
	},
	assessmentReport: {
		key: "assessmentReport",
		component: () => <AssessmentReport />,
		isAllowed: ({ instituteUuid, courseUuid, verityInstituteSettings, privileges }) =>
			!!instituteUuid &&
			!(verityInstituteSettings.liteMode && !verityInstituteSettings.liteModeViewReports) &&
			canAccessResource(
				{ privileges },
				{
					privilege: "PROCTORING_RESULTS",
					action: "READ",
					resourceScope: { instituteId: instituteUuid, courseId: courseUuid },
				},
			),
	},
	usageReport: {
		key: "usageReport",
		component: ({ instituteId }) => (instituteId ? <UsageReport instituteId={instituteId} /> : undefined),
		isAllowed: ({ instituteId, instituteUuid, verityInstituteSettings, privileges }) =>
			instituteId != null &&
			!(verityInstituteSettings.liteMode && !verityInstituteSettings.liteModeViewReports) &&
			canAccessResource(
				{ privileges },
				{
					privilege: "INSTITUTE_REPORT",
					action: "READ",
					resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
				},
			),
	},
	thirdPartyIntegration: {
		key: "thirdPartyIntegration",
		component: VerityThirdPartyIntegrationPortal,
		isAllowed: ({ lms, instituteUuid, privileges }) =>
			!!lms &&
			!!instituteUuid &&
			canAccessResource(
				{ privileges },
				{
					privilege: "INSTITUTE",
					action: "UPDATE",
					resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
				},
			),
	},
	instituteSettings: {
		key: "instituteSettings",
		component: VerityInstituteSettingsPortal,
		isAllowed: ({ instituteUuid, privileges }) =>
			!!instituteUuid &&
			canAccessResource(
				{ privileges },
				{
					privilege: "INSTITUTE",
					action: "UPDATE",
					resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
				},
			),
	},
	courseSettings: {
		key: "courseSettings",
		component: VerityCourseSettingsPortal,
		isAllowed: ({ instituteUuid, verityInstituteSettings, courseUuid, lmsProvidedCourseId, privileges }) =>
			!!instituteUuid &&
			!!lmsProvidedCourseId &&
			!verityInstituteSettings.liteMode &&
			canAccessResource(
				{ privileges },
				{
					privilege: "COURSE",
					action: "UPDATE",
					resourceScope: { instituteId: instituteUuid, courseId: courseUuid, resourceId: courseUuid },
				},
			),
	},
	integrationSettings: {
		key: "integrationSettings",
		component: ({ instituteId }) => (instituteId ? <IntegrationSettings instituteId={instituteId} /> : undefined),
		// TODO: remove lms check, temporary since there are no integration settings for blackboard or moodle yet...
		isAllowed: ({ instituteId, instituteUuid, privileges }) =>
			instituteId != null &&
			canAccessResource(
				{ privileges },
				{
					privilege: "INSTITUTE",
					action: "UPDATE",
					resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
				},
			),
	},
	scopeSettings: {
		key: "scopeSettings",
		component: InstituteScopeSettingsPortal,
		isAllowed: ({ lms, instituteUuid, privileges }) =>
			!!lms &&
			!!instituteUuid &&
			canAccessResource(
				{ privileges },
				{
					privilege: "INSTITUTE",
					action: "UPDATE",
					resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
				},
			),
	},
	userSettings: {
		key: "userSettings",
		component: UserSettingsPortal,
		isAllowed: ({ instituteId }) => instituteId != null,
	},
	mobileWorkflow: {
		key: "mobileWorkflow",
		component: () => <MobileWorkflow />,
	},
	rosterManagement: {
		key: "rosterManagement",
		isAllowed: ({ instituteUuid, privileges }) =>
			!!instituteUuid &&
			(canAccessResource(
				{ privileges },
				{
					privilege: "USER",
					action: "CREATE",
					resourceScope: { instituteId: instituteUuid },
				},
			) ||
				canAccessResource(
					{ privileges },
					{
						privilege: "USER",
						action: "UPDATE",
						resourceScope: { instituteId: instituteUuid },
					},
				)),
		component: () => <RosterPage />,
	},
	institutionInformation: {
		key: "institutionInformation",
		component: () => <InstitutionInformation />,
		isAllowed: ({ instituteUuid, privileges }) =>
			!!instituteUuid &&
			canAccessResource(
				{ privileges },
				{
					privilege: "INSTITUTE",
					action: "UPDATE",
					resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
				},
			),
	},
	marketplace: {
		key: "marketplace",
		component: () => <Marketplace />,
		isAllowed: ({ instituteUuid, privileges }) =>
			canAccessResource(
				{ privileges },
				{
					privilege: "INSTITUTE",
					action: "UPDATE",
					resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
				},
			),
	},
	latestUpdates: {
		key: "latestUpdates",
		component: () => <LatestUpdates />,
		isAllowed: ({ instituteUuid, privileges }) =>
			canAccessResource(
				{ privileges },
				{
					privilege: "INSTITUTE",
					action: "UPDATE",
					resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
				},
			),
	},
	takeAssessment: {
		key: "takeAssessment/:quizId",
		component: () => <TakeAssessmentPage />,
	},
};

export type PortalNavigationEntry = {
	key: string;
	text: string;
	icon: ReactNode;
	pageKey: PortalPageKey | CandidatePageKey;
};
export type PortalNavigationSection = {
	key: string;
	text: string;
	icon: ReactNode;
} & (
	| { entries: Record<string, PortalNavigationEntry>; pageKey?: undefined }
	| {
			entries?: undefined;
			pageKey: PortalPageKey | CandidatePageKey;
	  }
);
export type PortalNavigation = Record<string, PortalNavigationSection>;

export function usePortalNavigation() {
	const application = getProduct();
	const isAdmin = useIsAdmin();

	const portalNavigation: PortalNavigation = {
		tools: {
			key: "tools",
			icon: <AssessmentsSvg />,
			text: i18n.t("portal.portalTSX.assessments"),
			pageKey: "assessments",
		},
		live: {
			key: "live",
			text: i18n.t("portal.portalTSX.liveActivity"),
			icon: <LiveSvg />,
			entries: {
				liveSessions: {
					key: "liveSessions",
					text: i18n.t("portal.portalTSX.liveSchedule"),
					icon: <LiveScheduleSvg />,
					pageKey: "liveSessions",
				},
				livePopInMonitoring: {
					key: "livePopInMonitoring",
					text: i18n.t("portal.portalTSX.checkInMonitoring"),
					icon: <PopInMonitoringSvg />,
					pageKey: "livePopInMonitoring",
				},
			},
		},
		reports: {
			key: "reports",
			text: i18n.t("portal.portalTSX.report"),
			icon: <ReportsSvg />,
			entries: {
				institutionReport: {
					key: "institutionReport",
					text: i18n.t("portal.portalTSX.instituteReport"),
					icon: <InstitutionSvg />,
					pageKey: "institutionReport",
				},
				courseReport: {
					key: "courseReport",
					text: i18n.t("portal.portalTSX.courseReport"),
					icon: <BookIcon />,
					pageKey: "courseReport",
				},
				assessmentReport: {
					key: "assessmentReport",
					text: i18n.t("portal.portalTSX.assessmentReport"),
					icon: <CourseSvg />,
					pageKey: "assessmentReport",
				},
				usageReport: {
					key: "usageReport",
					text: "Usage Report",
					icon: <UsageSvg />,
					pageKey: "usageReport",
				},
			},
		},
		setup: {
			key: "setup",
			text: i18n.t("portal.portalTSX.setup"),
			icon: <SetupSvg />,
			entries: {
				extensionSetup: {
					key: "extensionSetup",
					text: i18n.t("portal.portalTSX.browserExtension"),
					icon: <ExtensionSvg />,
					pageKey: "extensionSetup",
				},
				lockdownSetup: {
					key: "lockdownSetup",
					text: i18n.t("portal.portalTSX.lockdownApp"),
					icon: <LockdownSvg />,
					pageKey: "lockdownSetup",
				},
				mobileSetup: {
					key: "mobileSetup",
					text: i18n.t("portal.portalTSX.mobileApp"),
					icon: <MobileSvg />,
					pageKey: "mobileSetup",
				},
			},
		},
		settings: {
			key: "settings",
			text: i18n.t("portal.portalTSX.settings"),
			icon: <SettingsSvg />,
			entries: {
				thirdPartyIntegration: {
					key: "thirdPartyIntegration",
					text: "Third Party Integration",
					icon: <ThirdPartyIntegrationSvg />,
					pageKey: "thirdPartyIntegration",
				},
				instituteSettings: {
					key: "instituteSettings",
					text: i18n.t("portal.portalTSX.instiSetting"),
					icon: <InstitutionSettingsSvg />,
					pageKey: "instituteSettings",
				},
				courseSettings: {
					key: "courseSettings",
					text: i18n.t("portal.portalTSX.courseSetting"),
					icon: <CourseSettingsSvg />,
					pageKey: "courseSettings",
				},
				integrationSettings: {
					key: "integrationSettings",
					text: i18n.t("portal.portalTSX.integrationSetting"),
					icon: <IntegrationSettingsSvg />,
					pageKey: "integrationSettings",
				},
				scopeSettings: {
					key: "scopeSettings",
					text: i18n.t("portal.portalTSX.scopeSettings"),
					icon: <ScopeSettingSvg />,
					pageKey: "scopeSettings",
				},
				userSettings: {
					key: "userSettings",
					text: i18n.t("portal.portalTSX.userSettings"),
					icon: <UserSettingsSvg />,
					pageKey: "userSettings",
				},
				rosterManagement: {
					key: "rosterManagement",
					text: i18n.t("portal.portalTSX.rosterManagement"),
					icon: <SettingsRosterIcon />,
					pageKey: "rosterManagement",
				},
				institutionInformation: {
					key: "institutionInformation",
					text: i18n.t("portal.portalTSX.institutionInformation"),
					icon: <InstitutionInformationSvg />,
					pageKey: "institutionInformation",
				},
			},
		},
		marketplace: {
			key: "marketplace",
			icon: <MarketplaceSvg />,
			text: i18n.t("portal.portalTSX.marketplace"),
			pageKey: "marketplace",
		},
		latestUpdates: {
			key: "latestUpdates",
			icon: <LatestUpdatesSvg />,
			text: i18n.t("portal.portalTSX.latestUpdates"),
			pageKey: "latestUpdates",
		},
	};

	const candidateNavigation: PortalNavigation = {
		interviews: {
			key: "interviews",
			icon: <HubSvg />,
			text: isAdmin ? i18n.t("candidate.sidebar.adminInterviews") : i18n.t("candidate.sidebar.instructorInterviews"),
			pageKey: "interviews",
		},
		results: {
			key: "results",
			icon: <ChecklistSvg />,
			text: i18n.t("candidate.sidebar.results"),
			pageKey: "results",
		},
		// reports: {
		// 	key: "reports",
		// 	icon: <GraphSvg />,
		// 	text: i18n.t("candidate.sidebar.reports"),
		// 	pageKey: "reports",
		// },
		configuration: {
			key: "configuration",
			icon: <ConfigurationSvg />,
			text: i18n.t("candidate.sidebar.configuration"),
			entries: {
				integrationSettings: {
					key: "integrationSettings",
					text: i18n.t("portal.portalTSX.integrationSetting"),
					icon: <IntegrationSettingsSvg />,
					pageKey: "integrationSettings",
				},
				rosterManagement: {
					key: "rosterManagement",
					text: i18n.t("portal.portalTSX.rosterManagement"),
					icon: <SettingsRosterIcon />,
					pageKey: "rosterManagement",
				},
				courseSettings: {
					key: "courseSettings",
					text: i18n.t("portal.portalTSX.courseSetting"),
					icon: <CourseSettingsSvg />,
					pageKey: "courseSettings",
				},
			},
		},
	};

	return application === "verity" ? portalNavigation : candidateNavigation;
}
